import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ENVIRONMENT_TOKEN } from '@aksia/infrastructure';

export type SecondFactorMethod =
  'Email' | 'Sms' | 'Authenticator' | 'RecoveryCode';

export interface AuthenticatorSetup {
  sharedKey: string;
  authenticatorUri: string;
  emailCodeSentTo?: string | null;
}

export interface AccountSecurityStatus {
  email?: string | null;
  phoneNumber?: string | null;
  phoneNumberConfirmed: boolean;
  smsAvailable: boolean;
  authenticatorEnabled: boolean;
  recoveryCodesLeft: number;
  preferredMethod?: SecondFactorMethod | null;
  usableMethods: SecondFactorMethod[];
}

@Injectable({
  providedIn: 'root',
})
export class SecondFactorService {
  private readonly httpClient = inject(HttpClient);
  private readonly environment = inject(ENVIRONMENT_TOKEN);

  private readonly formUrlEncodedHeaders = new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded',
  });

  //#region Login step (pending cookie)

  verify(method: SecondFactorMethod, code: string, rememberDevice: boolean) {
    return this.post<{ success: boolean; suggestAuthenticator?: boolean }>(
      '/account/second-factor/verify',
      {
        method,
        code,
        rememberDevice: String(rememberDevice),
      },
    );
  }

  send(method: SecondFactorMethod) {
    return this.post<{ success: boolean; maskedTarget?: string | null }>(
      '/account/second-factor/send',
      { method },
    );
  }

  setupAuthenticator() {
    return this.post<AuthenticatorSetup>(
      '/account/second-factor/authenticator/setup',
      {},
    );
  }

  confirmAuthenticator(
    code: string,
    emailCode: string,
    rememberDevice: boolean,
  ) {
    return this.post<{ success: boolean; recoveryCodes: string[] }>(
      '/account/second-factor/authenticator/confirm',
      { code, emailCode, rememberDevice: String(rememberDevice) },
    );
  }

  //#endregion

  //#region Self-service (SSO cookie)

  getSecurity(): Observable<AccountSecurityStatus> {
    return this.httpClient.get<AccountSecurityStatus>(
      `${this.environment.microservices_api_url}/account/security`,
      { withCredentials: true },
    );
  }

  startAuthenticatorSetup(password: string) {
    return this.post<AuthenticatorSetup>(
      '/account/security/authenticator/setup',
      { password },
    );
  }

  finishAuthenticatorSetup(code: string) {
    return this.post<{ success: boolean; recoveryCodes: string[] }>(
      '/account/security/authenticator/confirm',
      { code },
    );
  }

  removeAuthenticator(password: string) {
    return this.post<{ success: boolean }>(
      '/account/security/authenticator/remove',
      { password },
    );
  }

  regenerateRecoveryCodes(password: string) {
    return this.post<{ recoveryCodes: string[] }>(
      '/account/security/recovery-codes',
      { password },
    );
  }

  startPhoneChange(password: string, phoneNumber: string) {
    return this.post<{ success: boolean; maskedTarget?: string | null }>(
      '/account/security/phone',
      { password, phoneNumber },
    );
  }

  confirmPhoneChange(phoneNumber: string, code: string) {
    return this.post<{ success: boolean }>('/account/security/phone/confirm', {
      phoneNumber,
      code,
    });
  }

  removePhone(password: string) {
    return this.post<{ success: boolean }>('/account/security/phone/remove', {
      password,
    });
  }

  setPreferredMethod(method: SecondFactorMethod) {
    return this.post<{ success: boolean }>(
      '/account/security/preferred-method',
      { method },
    );
  }

  //#endregion

  private post<T>(path: string, fields: Record<string, string>) {
    let body = new HttpParams();
    for (const [key, value] of Object.entries(fields))
      body = body.set(key, value);

    return this.httpClient.post<T>(
      `${this.environment.microservices_api_url}${path}`,
      body.toString(),
      { headers: this.formUrlEncodedHeaders, withCredentials: true },
    );
  }
}

export const SECOND_FACTOR_LABELS: Record<SecondFactorMethod, string> = {
  Email: 'Email code',
  Sms: 'Text message (SMS)',
  Authenticator: 'Authenticator app',
  RecoveryCode: 'Recovery code',
};
